import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["minInput", "maxInput", "minRange", "maxRange", "progress", "currentMin", "currentMax"]
  static values = {
    min: Number,
    max: Number,
    gap: { type: Number, default: 500 }
  }

  connect() {
    this.absoluteMin = this.minValue
    this.absoluteMax = this.maxValue
    this.currentMinValue = this.minInputTarget ? Number(this.minInputTarget.value) : this.absoluteMin
    this.currentMaxValue = this.maxInputTarget ? Number(this.maxInputTarget.value) : this.absoluteMax

    this.clampValues()
    this.syncUI()
    this.emitChange()
  }

  previewFromInput(event) {
    const rawValue = event.target.value
    if (rawValue === "" || rawValue === "-") return

    const parsedValue = Number(rawValue)
    if (Number.isNaN(parsedValue)) return

    if (event.target === this.minInputTarget) {
      this.currentMinValue = parsedValue
    } else {
      this.currentMaxValue = parsedValue
    }

    this.clampValues(event.target === this.minInputTarget ? "min" : "max")
    this.syncRangesAndProgress()
  }

  updateFromInput(event) {
    const fallbackValue = event.target === this.minInputTarget ? this.currentMinValue : this.currentMaxValue
    const parsedValue = Number(event.target.value)
    const nextValue = Number.isNaN(parsedValue) ? fallbackValue : parsedValue

    if (event.target === this.minInputTarget) {
      this.currentMinValue = nextValue
    } else {
      this.currentMaxValue = nextValue
    }

    this.clampValues(event.target === this.minInputTarget ? "min" : "max")
    this.syncUI()
    this.emitChange()
  }

  updateFromRange(event) {
    if (event.target === this.minRangeTarget) {
      this.currentMinValue = Number(this.minRangeTarget.value)
    } else {
      this.currentMaxValue = Number(this.maxRangeTarget.value)
    }

    this.clampValues(event.target === this.minRangeTarget ? "min" : "max")
    this.syncUI()
    this.emitChange()
  }

  clampValues(changedSide = null) {
    this.currentMinValue = Math.max(this.absoluteMin, Math.min(this.currentMinValue, this.absoluteMax))
    this.currentMaxValue = Math.max(this.absoluteMin, Math.min(this.currentMaxValue, this.absoluteMax))

    if (this.currentMaxValue - this.currentMinValue < this.gapValue) {
      if (changedSide === "min") {
        this.currentMinValue = Math.max(this.absoluteMin, this.currentMaxValue - this.gapValue)
      } else {
        this.currentMaxValue = Math.min(this.absoluteMax, this.currentMinValue + this.gapValue)
      }
    }

    if (this.currentMaxValue - this.currentMinValue < this.gapValue) {
      this.currentMinValue = this.absoluteMin
      this.currentMaxValue = Math.min(this.absoluteMax, this.absoluteMin + this.gapValue)
    }
  }

  syncUI() {
    this.minInputTarget.value = this.currentMinValue
    this.maxInputTarget.value = this.currentMaxValue
    this.syncRangesAndProgress()

    if (this.hasCurrentMinTarget) {
      this.currentMinTarget.textContent = this.formatCurrency(this.currentMinValue)
    }
    if (this.hasCurrentMaxTarget) {
      this.currentMaxTarget.textContent = this.formatCurrency(this.currentMaxValue)
    }
  }

  syncRangesAndProgress() {
    this.minRangeTarget.value = this.currentMinValue
    this.maxRangeTarget.value = this.currentMaxValue

    const span = this.absoluteMax - this.absoluteMin || 1
    const left = ((this.currentMinValue - this.absoluteMin) / span) * 100
    const right = 100 - ((this.currentMaxValue - this.absoluteMin) / span) * 100

    this.progressTarget.style.left = `${left}%`
    this.progressTarget.style.right = `${right}%`
  }

  emitChange() {
    this.dispatch("change", {
      detail: {
        min: this.currentMinValue,
        max: this.currentMaxValue
      }
    })
  }

  formatCurrency(value) {
    return `R${Number(value).toLocaleString("en-ZA", { maximumFractionDigits: 0 })}`
  }
}
